import { getDeviceTypeTree } from '@/api/deviceType';
import { formatOptionData, getChildNodesByParentIds } from '@/shared/components/CascadingMultipleSelector/utils';
import { AlarmLevel, AlarmLevelDisplay, AlarmType, AlarmTypeDisplay, PushThirdApp } from '@/shared/types';
import { CustomFilter, Form, Input, Paging, Select, Table, useAsync, usePaging } from '@maxtropy/components';
import { Cascader } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';
import React, { FC, Key, useEffect, useMemo, useState } from 'react';
import { ruleModalColumns } from './util';
import { apiV2RuleAlarmPushRulePagePost } from '@maxtropy/device-customer-apis-v2';

const { Option } = Select;

export interface ISelectRuleModal {
  value?: any[];
  onChange?: (value: any[]) => void;
  disabledIds?: Key[];
}

interface SearchParams {
  nameOrCode?: string;
  groupNameOrCode?: string;
  deviceTypeIdList?: number[];
  alarmType?: AlarmLevel;
  type?: AlarmType;
}

const SelectRuleModal: FC<ISelectRuleModal> = props => {
  const { value, onChange, disabledIds } = props;
  const [form] = Form.useForm();

  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const deviceTypeData = useAsync(getDeviceTypeTree);
  const [ruleList, setRuleList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [alarmRuleIds, setSelectRowsKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    setLoading(true);
    apiV2RuleAlarmPushRulePagePost({ ...searchParams, page: pageOffset, size: pageSize }).then(res => {
      setLoading(false);
      if (res) {
        setTotalCount(res?.total ?? 0);
        setRuleList(
          (res.list ?? []).map(i => ({
            ...i,
            ruleName: i.name,
            ruleCode: i.code,
            ruleId: i.id,
          }))
        );
      }
      setSelectRowsKeys((value ?? []).map(i => i.ruleId));
    });
  }, [pageOffset, pageSize, searchParams, setTotalCount]);

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const onFinish = (search: SearchParams) => {
    const deviceTypeIds = getChildNodesByParentIds(
      search?.deviceTypeIdList as unknown as number[][],
      deviceTypeData?.tree
    );
    setSearchParams({
      ...search,
      deviceTypeIdList: deviceTypeIds,
    });
    setPageOffset(1);
  };

  const onReset = () => {
    setSearchParams({});
    form.setFieldsValue({
      nameOrCode: undefined,
      groupNameOrCode: undefined,
      deviceTypes: undefined,
      type: undefined,
      alarmType: undefined,
    });
    setPageOffset(1);
  };

  const filters = (
    <CustomFilter colSpan={8} form={form} onFinish={onFinish} onReset={onReset}>
      <Form.Item name="nameOrCode" label="规则编号/名称">
        <Input placeholder="请输入编号或者名称查询" />
      </Form.Item>
      <Form.Item name="groupNameOrCode" label="规则组名称">
        <Input placeholder="请输入规则组名称查询" />
      </Form.Item>
      <Form.Item name="deviceTypeIdList" label="所属类目">
        <Cascader
          options={formatOptionData(deviceTypeData)}
          allowClear={false}
          fieldNames={{ children: 'child' }}
          multiple
          maxTagCount="responsive"
          showSearch={{ filter }}
          placeholder={'请选择所属类目'}
        />
      </Form.Item>
      <Form.Item name="alarmType" label="报警等级">
        <Select placeholder="请选择">
          <Option value={AlarmLevel.HIGHEST}>{AlarmLevelDisplay[AlarmLevel.HIGHEST]}</Option>
          <Option value={AlarmLevel.HIGH}>{AlarmLevelDisplay[AlarmLevel.HIGH]}</Option>
          <Option value={AlarmLevel.MIDDLE}>{AlarmLevelDisplay[AlarmLevel.MIDDLE]}</Option>
          <Option value={AlarmLevel.LOW}>{AlarmLevelDisplay[AlarmLevel.LOW]}</Option>
          <Option value={AlarmLevel.LOWEST}>{AlarmLevelDisplay[AlarmLevel.LOWEST]}</Option>
        </Select>
      </Form.Item>
    </CustomFilter>
  );

  const rowSelection = {
    preserveSelectedRowKeys: true,
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      const selectedRowMap = new Map<Key, any>();
      (value ?? []).forEach(item => {
        if (item?.ruleId) {
          selectedRowMap.set(item.ruleId, item);
        }
      });
      ruleList.forEach(item => {
        if (item?.ruleId) {
          selectedRowMap.set(item.ruleId, item);
        }
      });
      selectedRows.forEach(item => {
        if (item?.ruleId) {
          selectedRowMap.set(item.ruleId, item);
        }
      });

      const newDatas = selectedRowKeys
        .map(ruleId => selectedRowMap.get(ruleId))
        .filter(Boolean)
        .map(i => ({
          ...i,
          ruleName: i.ruleName ?? i.name,
          ruleCode: i.ruleCode ?? i.code,
          thirdPartyAppId: i.thirdPartyAppId ?? [PushThirdApp.WEB_SOCKET],
          resumeAlarmIdList: i.resumeAlarmIdList ?? [],
        }));

      setSelectRowsKeys(selectedRowKeys);

      onChange && onChange(newDatas);
    },
    getCheckboxProps: (record: any) => ({
      disabled: (disabledIds ?? []).includes(record.ruleId),
    }),
    selectedRowKeys: alarmRuleIds,
  };

  return (
    <>
      {filters}
      <Table
        loading={loading}
        rowSelection={{
          type: 'checkbox',
          // hideSelectAll: true,
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey="ruleId"
        columns={ruleModalColumns}
        dataSource={ruleList}
        showSelectedCount
        selectedCount={(value ?? []).length}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </>
  );
};

export default SelectRuleModal;
